// RootTemplateInput.js (z klawiaturą, jak w FractionInput + mechanizm "touched")
// Szablon: [stopień]√(radicand)
// API: onChange({ raw, parsed }) — nie emituje przy starcie; dopiero po interakcji użytkownika.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { InlineMath } from "react-katex";
import SetKeyboard from "./SetKeyboard";

/* ======================== Helpers ======================== */

function toLatexPreview(deg, inside) {
  const q = String(deg || "").trim();
  const rad = String(inside || "").trim();

  let niceRad = rad
    .replace(/\s+/g, "")
    .replace(/sqrt\(([^)]+)\)/gi, (_m, x) => `\\sqrt{${x}}`)
    .replace(/sqrt\[(\d+)\]\(([^)]+)\)/gi, (_m, d, x) => `\\sqrt[${d}]{${x}}`)
    .replace(/^(\d+)\^\((-?\d+)\/(\d+)\)$/, (_m, a, p, qq) => `${a}^{\\tfrac{${p}}{${qq}}}`)
    .replace(/^(\d+)\^\-?(\d+)\/(\d+)$/, (_m, a, p, qq) => {
      const sign = rad.includes("^-") ? "-" : "";
      return `${a}^{\\tfrac{${sign}${p}}{${qq}}}`;
    });

  return q && q !== "2"
    ? `\\sqrt[${q}]{${niceRad || "\\;"}}`
    : `\\sqrt{${niceRad || "\\;"}}`;
}

function parseIncomingValue(value) {
  const raw = (value && typeof value === "object" && "raw" in value ? value.raw : value) || "";
  const s = String(raw).replace(/\s+/g, "");
  let m = s.match(/^sqrt\((.+)\)$/i);
  if (m) return { degree: "2", inside: m[1] };
  m = s.match(/^sqrt\[(\d+)\]\((.+)\)$/i);
  if (m) return { degree: m[1], inside: m[2] };
  return { degree: "", inside: "" };
}

/* ======================== Component ======================== */

export default function RootTemplateInput({
  value,
  onChange,
  disabled,
  placeholderInside = "np. 49, 125",
  placeholderDegree = "q",
}) {
  // stan wejściowy -> stan lokalny
  const { degree: d0, inside: i0 } = useMemo(() => parseIncomingValue(value), [value]);
  const [degree, setDegree] = useState(d0);
  const [inside, setInside] = useState(i0);

  // emisja dopiero po realnej interakcji
  const [touched, setTouched] = useState(false);

  // klawiatura jak w FractionInput
  const [kbdOpen, setKbdOpen] = useState(false);

  const degRef = useRef(null);
  const inRef = useRef(null);

  // na zmianę value z zewnątrz resetujemy pola i "touched"
  useEffect(() => {
    const { degree: nd, inside: ni } = parseIncomingValue(value);
    setDegree(nd);
    setInside(ni);
    setTouched(false);
  }, [value]);

  // emituj do rodzica tylko po interakcji użytkownika
  useEffect(() => {
    if (!touched) return;

    const q = String(degree || "").trim();
    const rad = String(inside || "").trim();

    if (!q && !rad) {
      onChange && onChange({ raw: "", parsed: null });
      return;
    }

    // NOWE: jeśli środek jest pusty, nic nie wysyłamy – zostawiamy stan lokalny
    if (!rad) {
      return;
    }

    const raw = q && q !== "2" ? `sqrt[${q}](${rad})` : `sqrt(${rad})`;
    const parsed = {
      degree: q && !isNaN(+q) ? +q : null,
      inside: rad,
    };
    onChange && onChange({ raw, parsed });
  }, [degree, inside, touched, onChange]);

  // Handlery pól (ustawiają touched)
  const handleDegree = (e) => {
    setTouched(true);
    setDegree(e.target.value.replace(/\D/g, ""));
  };
  const handleInside = (e) => {
    setTouched(true);
    setInside(e.target.value);
  };

  // ====== SetKeyboard — identyczny układ jak w FractionInput ======
  const setCaret = (el, pos) => {
    if (!el) return;
    el.focus();
    el.setSelectionRange(pos, pos);
  };

  const insertIntoInside = (txt) => {
    const el = inRef.current;
    const current = inside ?? "";
    if (!el) return;

    const s = el.selectionStart ?? current.length;
    const e = el.selectionEnd ?? current.length;
    const next = current.slice(0, s) + txt + current.slice(e);
    setTouched(true);
    setInside(next);
    setTimeout(() => setCaret(el, s + txt.length), 0);
  };

  const handleKbdAction = (type, payload) => {
    const el = inRef.current;
    if (!el) return;
    const current = inside ?? "";
    const s = el.selectionStart ?? 0;
    const e = el.selectionEnd ?? 0;

    if (type === "insert") {
      insertIntoInside(String(payload ?? ""));
      return;
    }
    if (type === "backspace") {
      setTouched(true);
      if (s !== e) setInside(current.slice(0, s) + current.slice(e));
      else if (s > 0) setInside(current.slice(0, s - 1) + current.slice(e));
      setTimeout(() => setCaret(el, Math.max(0, s - (s === e ? 1 : 0))), 0);
      return;
    }
    if (type === "delete") {
      setTouched(true);
      if (s !== e) setInside(current.slice(0, s) + current.slice(e));
      else if (e < current.length) setInside(current.slice(0, s) + current.slice(e + 1));
      setTimeout(() => setCaret(el, s), 0);
      return;
    }
    if (type === "moveLeft" || type === "moveRight") {
      const delta = type === "moveLeft" ? -1 : 1;
      const pos = Math.max(0, Math.min((el.selectionEnd ?? 0) + delta, current.length));
      setTimeout(() => setCaret(el, pos), 0);
      return;
    }
    if (type === "clear") {
      setTouched(true);
      setInside("");
      setTimeout(() => el.focus(), 0);
      return;
    }
    if (type === "enter") {
      return;
    }
    if (type === "toggle") {
      setKbdOpen((v) => !v);
      return;
    }
  };

  // ====== render ======
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
      {/* rząd: toggle + template */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <SetKeyboard
          render="toggle"
          targetRef={inRef}
          open={kbdOpen}
          onOpenChange={setKbdOpen}
          onAction={handleKbdAction}
          compact
        />

        <div
          className="input-secondary root-template"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 10px",
            borderRadius: 10,
          }}
        >
          <input
            ref={degRef}
            type="text"
            inputMode="numeric"
            value={degree}
            onChange={handleDegree}
            disabled={disabled}
            placeholder={placeholderDegree}
            className="input-secondary"
            style={{ width: 36, textAlign: "center", padding: "6px 6px" }}
            aria-label="Stopień pierwiastka"
          />
          <span style={{ fontSize: 22, lineHeight: "22px", transform: "translateY(-1px)" }}>√</span>
          <span style={{ opacity: 0.8 }}>(</span>
          <input
            ref={inRef}
            type="text"
            value={inside}
            onChange={handleInside}
            disabled={disabled}
            placeholder={placeholderInside}
            className="input-secondary"
            style={{ minWidth: 180, padding: "6px 8px" }}
            aria-label="Zawartość pierwiastka"
          />
          <span style={{ opacity: 0.8 }}>)</span>
        </div>
      </div>

      {kbdOpen && (
        <div style={{ width: "100%" }}>
          <SetKeyboard
            render="panel"
            targetRef={inRef}
            open={kbdOpen}
            onOpenChange={setKbdOpen}
            onAction={handleKbdAction}
            compact
            panelIndent={36}
          />
        </div>
      )}

      {(degree || inside) && (
        <div style={{ marginTop: 6 }}>
          <strong>Podgląd:</strong>{" "}
          <InlineMath math={toLatexPreview(degree, inside)} />
        </div>
      )}
    </div>
  );
}
